import React, { ReactElement, useCallback, useState } from 'react';
import Image from 'next/image';
import { Heading, Button } from '@appsumo/dorado-react';
import { useToggle } from 'usehooks-ts';
import clsx from 'clsx';

import { useEvent } from '~/lib/events';
import { STEP, CREDIT_COMPONENT_TYPES } from '~/constants/checkout';
import { useCheckoutState, useCheckoutAPI } from '~/hooks/checkout';

import { Credits } from '~/components/checkout/CreditsCoupons/Credits';
import {
  CollapsedSummary,
  CouponControls,
  ManualCoupon,
  SavedCoupons,
} from '~/components/checkout/CreditsCoupons';
import { CheckoutCard } from '~/components/checkout/Global';
import Exclamation from '~/public/font-awesome/exclamation-circle-red.png';

const DetailMemoized = React.memo(
  function CreditsCouponsDetails(): ReactElement {
    const { step, discountData, cart, stepErrorMessageObject } =
      useCheckoutState();
    const { setStep, setStepErrorMessage } = useCheckoutAPI();
    const [showManualCoupon, toggleManualCoupon] = useToggle(
      !cart?.coupon?.coupon_id,
    );
    const [isToggleMore, toggleMore] = useToggle();
    const [lastCoupon, setLastCoupon] = useState<string>('');
    const couponSize = discountData?.coupons?.length ?? 0;
    const hasError = !!stepErrorMessageObject[STEP.CREDITS_COUPONS];

    // Move the coupon to the top of the list if there was an error after applying it and checking out,
    // but does not remove it from user's account (coupon isn't "expired" or otherwise invalid)
    useEvent('coupon:lastAdded', (coupon) => {
      setLastCoupon(coupon);
    });

    const onClickContinue = useCallback(() => {
      setStep(STEP.PAYMENT);
      // Clear any coupon/credit step errors that may not have been resolved, does not affect actual checkout
      setStepErrorMessage(STEP.CREDITS_COUPONS, '');
    }, [setStep, setStepErrorMessage]);

    if (step !== STEP.CREDITS_COUPONS) return <></>;

    return (
      <div className="grid w-full gap-y-4">
        <div className="flex items-center">
          {hasError && (
            <Image
              src={Exclamation}
              alt="Exclamation"
              width={18}
              height={18}
              className="mr-2 mt-1" // mt-1 centers vertically slightly better
            />
          )}
          <Heading.H3>Credits & coupons</Heading.H3>
        </div>
        <Credits cart={cart} type={CREDIT_COMPONENT_TYPES.EXPANDED} />
        <SavedCoupons
          appliedCouponId={cart?.coupon?.coupon_id}
          autoAppliedCouponDesc={
            cart?.active_auto_campaign_coupon_applied
              ? cart.coupon_desc ?? 'a discount'
              : ''
          }
          coupons={discountData?.coupons}
          isToggled={isToggleMore}
          lastCouponCode={lastCoupon}
        />
        <CouponControls
          toggleManualCoupon={toggleManualCoupon}
          showManualCoupon={showManualCoupon}
          couponSize={couponSize}
          isToggleMore={isToggleMore}
          toggleMore={toggleMore}
        />
        {(!couponSize || showManualCoupon) && (
          <ManualCoupon isCouponApplied={!!cart?.coupon?.coupon_id} />
        )}
        <Button
          className="flex w-full justify-center md:w-fit"
          disabled={false}
          onClick={onClickContinue}
        >
          <span>Continue to payment method</span>
        </Button>
      </div>
    );
  },
);

const MinimizedMemoized = React.memo(
  function CreditsCouponsMinimized(): ReactElement {
    const { step, discountData, cart } = useCheckoutState();
    const { setStep, getStepValidation } = useCheckoutAPI();

    const onClickCallback = useCallback(() => {
      setStep(STEP.CREDITS_COUPONS);
    }, [setStep]);

    if (step === STEP.CREDITS_COUPONS) return <></>;

    const isBillingValidated = getStepValidation(STEP.BILLING);
    const showAppliedDiscounts = isBillingValidated;
    const headingClassName = clsx(!showAppliedDiscounts && 'text-slate');

    return (
      <div className="grid w-full gap-y-4">
        <div className="flex w-full">
          <div className="grow">
            <Heading.H3 className={headingClassName}>
              Credits & coupons
            </Heading.H3>
          </div>
          {isBillingValidated && (
            <button
              onClick={onClickCallback}
              className="text-bolt hover:underline"
            >
              Edit
            </button>
          )}
        </div>
        {isBillingValidated && (
          <CollapsedSummary
            cart={cart}
            hasCoupons={!!discountData?.coupons?.length}
            onClickCoupon={onClickCallback}
          />
        )}
      </div>
    );
  },
);

export default function CreditsCoupons(): ReactElement {
  return (
    <CheckoutCard step={STEP.CREDITS_COUPONS}>
      <MinimizedMemoized />
      <DetailMemoized />
    </CheckoutCard>
  );
}
